/**
 * Dice Face Constants
 * Rotation mappings and notch positions for standard six-sided dice
 */

/**
 * Face rotation mappings for box geometry
 * 
 * Maps each face value (1-6) to the rotation needed to show that face
 * toward the camera (+Z direction in flat mode, +Y direction in vertical mode).
 * 
 * These rotations work when the scene is NOT rotated (flat mode, sceneRotation = [0,0,0]).
 * When scene is rotated (e.g., vertical mode with sceneRotation = [-π/2,0,0]),
 * the DicePhysicsService adjusts these mappings accordingly.
 */
export const DICE_FACE_ROTATIONS: Record<number, [number, number, number]> = {
  1: [Math.PI / 2, 0, 0],       // Top face (Y+) rotated to front (Z+)
  2: [0, 0, 0],                  // Front face (Z+) already facing camera
  3: [0, Math.PI / 2, 0],        // Left face (X-) rotated to front
  4: [0, -Math.PI / 2, 0],       // Right face (X+) rotated to front
  5: [0, Math.PI, 0],            // Back face (Z-) rotated to front
  6: [-Math.PI / 2, 0, 0]        // Bottom face (Y-) rotated to front
};

/**
 * Alternative rotation map for vertical mode
 * 
 * When scene is rotated -90° on X-axis (vertical AR mode),
 * the target face changes from Z+ to Y+.
 */
export const DICE_FACE_ROTATIONS_VERTICAL: Record<number, [number, number, number]> = {
  1: [0, 0, 0],                  // Top face (Y+) already facing up
  2: [Math.PI / 2, 0, 0],        // Front face (Z+) rotated to top
  3: [0, 0, -Math.PI / 2],       // Left face (X-) rotated to top
  4: [0, 0, Math.PI / 2],        // Right face (X+) rotated to top
  5: [-Math.PI / 2, 0, 0],       // Back face (Z-) rotated to top
  6: [Math.PI, 0, 0]             // Bottom face (Y-) flipped to top
};

/**
 * Notch (dot) positions for each face
 * 
 * Positions are relative to face center, in local face coordinates:
 * - X: left (-) to right (+)
 * - Y: bottom (-) to top (+)
 * - Z: depth into face (always 0.5 for surface positioning)
 * 
 * Standard die configuration:
 * - Opposite faces sum to 7 (1-6, 2-5, 3-4)
 * - Faces 1,2,3 meet at a corner (right-handed orientation)
 */
export const NOTCH_POSITIONS: Record<number, Array<[number, number, number]>> = {
  // Face 1: Single center dot
  1: [
    [0, 0, 0.5]
  ],
  
  // Face 2: Two diagonal dots (bottom-left to top-right)
  2: [
    [-0.2, -0.2, 0.5],
    [0.2, 0.2, 0.5]
  ],
  
  // Face 3: Three diagonal dots
  3: [
    [-0.2, -0.2, 0.5],
    [0, 0, 0.5],
    [0.2, 0.2, 0.5]
  ],
  
  // Face 4: Four corner dots
  4: [
    [-0.2, -0.2, 0.5],
    [0.2, -0.2, 0.5],
    [-0.2, 0.2, 0.5],
    [0.2, 0.2, 0.5]
  ],
  
  // Face 5: Four corners plus center
  5: [
    [-0.2, -0.2, 0.5],
    [0.2, -0.2, 0.5],
    [0, 0, 0.5],
    [-0.2, 0.2, 0.5],
    [0.2, 0.2, 0.5]
  ],
  
  // Face 6: Six dots in two columns
  6: [
    [-0.2, -0.2, 0.5],
    [0.2, -0.2, 0.5],
    [-0.2, 0, 0.5],
    [0.2, 0, 0.5],
    [-0.2, 0.2, 0.5],
    [0.2, 0.2, 0.5]
  ]
};

/**
 * Default physics parameters
 * Using Z-down gravity for game board coordinate system (AR compatible)
 */
export const DEFAULT_PHYSICS_OPTIONS = {
  gravity: [0, 0, -50] as [number, number, number],
  restitution: 0.3,
  friction: 0.1,
  sleepTimeLimit: 0.1,  // Increased from 0.02 to 0.1 seconds (~6 frames)
  sleepSpeedLimit: 0.05, // Reduced from 0.1 to 0.05 (stricter)
  maxAttempts: 100,
  timeout: 3000
};

/**
 * Default visual parameters
 */
export const DEFAULT_VISUAL_OPTIONS = {
  color: '#FFFFFF',
  notchColor: '#000000',
  roughness: 0.3,
  metalness: 0.1,
  castShadow: true
};

/**
 * Default dice configuration
 */
export const DEFAULT_DICE_CONFIG = {
  diceCount: 1,
  mode: 'predetermined' as const,
  size: 0.6,
  edgeRadius: 0.08,
  segments: 40,
  notchDepth: 0.17,
  notchRadius: 0.15,
  gravity: [0, 0, -30] as [number, number, number],
  sceneRotation: [0, 0, 0] as [number, number, number],
  floorPosition: [0, 0, 0.1] as [number, number, number], // Floor center at Z=0.1 (top surface at Z=0.2)
  startPosition: [0, 0, 1.5] as [number, number, number]  // Die starts well above floor
};
